import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiPlus, HiXMark } from "react-icons/hi2";
import { EVENT_MAX_TAGS, EVENT_TAG_MAX_LENGTH } from "../../types/event";
import {
  canAddTag,
  filterTagOptions,
  normaliseTagName,
  tagKey,
} from "../../utils/eventTagUtils";
import { floatingLabelClass } from "./FloatingField";

interface TagSelectProps {
  /** Selected tag display names. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Tag names to suggest (e.g. tags already used on the athlete's events). */
  suggestions: string[];
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  maxTags?: number;
  maxLength?: number;
  id?: string;
}

type Item = { kind: "option" | "create"; name: string };

/**
 * Multi-select combobox with creatable options. Focus / click opens the
 * suggestions; typing filters them and offers `Create "<text>"` when nothing
 * matches. Selected tags render as removable chips under the label; Backspace
 * on an empty input removes the last one. Styled like the other label-inside
 * fields; the list renders in a portal so the sheet can't clip it.
 */
const TagSelect: React.FC<TagSelectProps> = ({
  value,
  onChange,
  suggestions,
  label,
  placeholder = "Search or create tags…",
  disabled = false,
  maxTags = EVENT_MAX_TAGS,
  maxLength = EVENT_TAG_MAX_LENGTH,
  id,
}) => {
  const autoId = useId();
  const inputId = id || `${autoId}-input`;
  const listId = `${autoId}-listbox`;
  const optionId = (i: number) => `${autoId}-opt-${i}`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const atLimit = value.length >= maxTags;

  const items: Item[] = useMemo(() => {
    const { options, createName } = filterTagOptions(suggestions, value, query);
    const list: Item[] = options.map((name) => ({ kind: "option", name }));
    if (createName) list.push({ kind: "create", name: createName });
    return list;
  }, [suggestions, value, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        rootRef.current &&
        !rootRef.current.contains(target) &&
        !(listRef.current && listRef.current.contains(target))
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Keep the highlighted option scrolled into view.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document
      .getElementById(optionId(activeIndex))
      ?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, open]);

  const add = (raw: string) => {
    const name = normaliseTagName(raw, maxLength);
    if (!canAddTag(value, name, maxTags)) return;
    // Prefer the suggestion's spelling when the text matches an existing tag.
    const saved = suggestions.find((t) => tagKey(t) === name.toLowerCase());
    onChange([...value, saved ? normaliseTagName(saved, maxLength) : name]);
    setQuery("");
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const remove = (name: string) => {
    onChange(value.filter((t) => t !== name));
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) setOpen(true);
        if (items.length > 0) setActiveIndex((i) => (i + 1) % items.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) setOpen(true);
        if (items.length > 0)
          setActiveIndex((i) => (i <= 0 ? items.length - 1 : i - 1));
        break;
      case "Enter": {
        // Never let Enter submit the surrounding form.
        e.preventDefault();
        e.stopPropagation();
        if (!open) {
          setOpen(true);
          break;
        }
        const active = activeIndex >= 0 ? items[activeIndex] : undefined;
        if (active) add(active.name);
        else if (query.trim()) add(query);
        break;
      }
      case "Escape":
        if (open) {
          // Close the list only; keep the surrounding sheet open.
          e.preventDefault();
          e.nativeEvent.stopPropagation();
          setOpen(false);
          setQuery("");
          setActiveIndex(-1);
        }
        break;
      case "Backspace":
        if (!query && value.length > 0) {
          e.preventDefault();
          onChange(value.slice(0, -1));
        }
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const showList = open && !disabled;
  const activeDescendant =
    showList && activeIndex >= 0 && activeIndex < items.length
      ? optionId(activeIndex)
      : undefined;

  // Position the portal dropdown below the control; follow it while the sheet
  // scrolls, the window resizes or chips change the control's height.
  useEffect(() => {
    if (!showList) return;
    const update = () => {
      const el = controlRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [showList, value.length, query]);

  return (
    <div ref={rootRef}>
      <div className="relative">
        <div
          ref={controlRef}
          onClick={() => {
            if (disabled) return;
            inputRef.current?.focus();
            setOpen(true);
          }}
          className={`flex flex-wrap items-center gap-1.5 w-full min-h-[3.25rem] border border-base-300 rounded-md bg-base-300 text-base-content text-sm font-grotesk px-4 cursor-text ${
            label ? "pt-6 pb-2" : "py-4"
          } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 max-w-full rounded-full bg-primary/10 ring-1 ring-primary/30 pl-2.5 pr-1 py-0.5 text-xs font-semibold text-base-content"
            >
              <span className="truncate">{tag}</span>
              <button
                type="button"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  remove(tag);
                }}
                aria-label={`Remove tag ${tag}`}
                className="p-0.5 rounded-full text-base-content/50 hover:text-base-content hover:bg-base-content/10 transition-colors"
              >
                <HiXMark className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={activeDescendant}
            autoComplete="off"
            value={query}
            maxLength={maxLength}
            disabled={disabled}
            placeholder={value.length === 0 ? placeholder : ""}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(-1);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            // text-base on mobile avoids the iOS focus zoom.
            className="flex-1 min-w-[6rem] h-5 leading-5 bg-transparent outline-none border-0 focus:ring-0 p-0 text-base sm:text-sm font-semibold font-grotesk text-base-content placeholder:font-normal placeholder:text-base-content/40"
          />
        </div>
        {label && (
          <label htmlFor={inputId} className={floatingLabelClass}>
            {label}
          </label>
        )}

        {showList &&
          dropdownPos &&
          createPortal(
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              aria-multiselectable="true"
              aria-label={label || "Tags"}
              // Keep focus in the input when clicking the list / its scrollbar.
              onMouseDown={(e) => e.preventDefault()}
              className="fixed z-[9999] bg-base-100 border border-base-300 rounded-xl shadow-lg py-1 max-h-60 overflow-y-auto"
              style={{
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
              }}
            >
              {items.map((item, i) => (
                <div
                  key={`${item.kind}-${item.name}`}
                  id={optionId(i)}
                  role="option"
                  aria-selected={false}
                  aria-disabled={atLimit || undefined}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => add(item.name)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 text-sm font-grotesk text-left transition-colors ${
                    atLimit ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                  } ${
                    i === activeIndex
                      ? "bg-base-200 text-base-content"
                      : "text-base-content/70 hover:bg-base-200"
                  }`}
                >
                  {item.kind === "create" ? (
                    <>
                      <HiPlus className="w-4 h-4 flex-shrink-0 text-primary" />
                      <span className="truncate">
                        Create “
                        <span className="font-semibold text-base-content">
                          {item.name}
                        </span>
                        ”
                      </span>
                    </>
                  ) : (
                    <span className="truncate">{item.name}</span>
                  )}
                </div>
              ))}
              {items.length === 0 && (
                <p className="px-3 py-2 text-sm text-base-content/40 font-grotesk">
                  {suggestions.length === 0
                    ? "No saved tags yet. Type to create one."
                    : "No more tags. Type to create one."}
                </p>
              )}
              {atLimit && (
                <p className="px-3 py-2 text-xs text-base-content/50 font-grotesk border-t border-base-300">
                  Limit of {maxTags} tags reached
                </p>
              )}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default TagSelect;
